import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EmptyState from "@/shared/components/ui/empty-state/empty-state.tsx";
import { useGetMapOverlay, useGetMapOverlayVersions } from "@/map/api/queries/overlay.queries";
import { MapOverlayControls, MapOverlayVersionSelector } from "@/map/components/map-overlay";
import { Button } from "@/ui/components/ui/button";
import { Crosshair, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useMapResize, useMapTooltip } from "@/map/hooks";
import type { CoordinateSet } from "@/map/types/map";
import { calculateMapCenter, shouldShowEmptyState } from "@/map/utils/mapCalculations";
import { DEFAULT_MAP_VIEW_OPTIONS } from "@/map/utils/mapConstants";
import type { MapViewOptions } from "@/map/utils/map-view-options";
import PolygonControls from "@/polygon/components/polygon-controls.tsx";
import PolygonLegend from "@/polygon/components/polygon-legend.tsx";
import { useGetPolygonsByIdsForSelector } from "@/polygon/api/queries/polygon-selector.queries";
import { stringToColor } from "@/shared/utils/color/string-to-color";
import { parseSelectionId } from "@/map/utils/map-coordinates";
import { useMapProviderStore } from "@/map/stores/useMapProviderStore";
import GoogleMapCanvas from "./GoogleMapCanvas";
import LibreMapCanvas from "./LibreMapCanvas";
import type { MapCanvasHandle } from "./map-canvas.types";

/** Zoom level the map animates to when a marker is clicked (only zooms in). */
const MARKER_FOCUS_ZOOM = 17;

/**
 * Closest practical zoom used when focusing an alert (alert marker click or an
 * alert-segment click on the timeline) — lands right on top of it. Google Maps
 * supports up to ~21; 20 is a strong, safe default. Tune here.
 */
const MAX_ALERT_ZOOM = 20;

/**
 * Below this zoom a soft focus is treated as "too far" and zooms in even when
 * the point is technically inside the viewport bounds — so stepping into a
 * pin from a zoomed-out view still lands close, while nearby stepping (already
 * zoomed in) just pans.
 */
const SOFT_FOCUS_MIN_ZOOM = 15;

/**
 * A camera-focus intent from the page. `hard` always pans + zooms to
 * {@link MARKER_FOCUS_ZOOM}; `max` pans + zooms all the way to
 * {@link MAX_ALERT_ZOOM} (used for alert selections); `soft` always pans but
 * only zooms when the point is off-screen or the current view is too far out.
 * `nonce` lets the page re-request focus on the same coords (repeat selections
 * still fire).
 */
export type MapFocusRequest = {
  lat: number;
  lng: number;
  mode: "hard" | "soft" | "max";
  nonce: number;
};

type MapViewProps = {
  basicCoordinates: CoordinateSet[];
  options?: MapViewOptions;
  resizable?: boolean;
  initialHeight?: number;
  storageKey?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  loadingText?: string;
  locationId?: number;
  overlayVersion?: string;
  showPolygonControls?: boolean;
  renderLeftContent?: React.ReactNode;
  polygonCoordinates?: CoordinateSet[];
  controlsUIEnable?: boolean;
  onClickPoint?: (date: Date) => void;
  /**
   * Fired when a marker is clicked, with its `selectionId` (or fallback marker
   * id). Lets callers select a location/machine/alert pin. Additive — existing
   * `onClickPoint` (time-based) behavior is unchanged.
   */
  onMarkerSelect?: (markerId: string) => void;
  /**
   * Marker id (selectionId) to emphasize — every other alert marker dims to
   * half opacity. Drive it with the hovered-or-selected alert.
   */
  emphasizedMarkerId?: string | null;
  /** Mouse enter/leave on a marker — lets the page mirror map hover into lists. */
  onMarkerHover?: (markerId: string, hovering: boolean) => void;
  /**
   * Imperative camera-focus intent. Selecting an alert (feed/arrow) sets this so
   * the map moves to it; the `nonce` makes repeat requests to the same coords
   * re-fire. See {@link MapFocusRequest}.
   */
  focusRequest?: MapFocusRequest | null;
  /**
   * Hide the floating on-map controls (overlay version / overlay toggle /
   * polygon controls). Use when those controls are hosted elsewhere (e.g. a
   * settings panel) while MapView still draws the overlay + polygons.
   */
  hideLeftControls?: boolean;
  /* --- Optional controlled state (falls back to internal when omitted) --- */
  overlayEnabled?: boolean;
  onOverlayEnabledChange?: (enabled: boolean) => void;
  selectedOverlayVersion?: string;
  onOverlayVersionChange?: (version: string) => void;
  selectedPolygonIds?: number[];
  onSelectedPolygonIdsChange?: (ids: number[]) => void;
};

const MapView: React.FC<MapViewProps> = ({
  basicCoordinates,
  options,
  resizable = false,
  initialHeight = 700,
  storageKey,
  isLoading = false,
  isEmpty,
  emptyStateTitle,
  emptyStateDescription,
  loadingText,
  locationId,
  overlayVersion,
  showPolygonControls = false,
  renderLeftContent,
  polygonCoordinates: polygonCoordinatesProp = [],
  controlsUIEnable = false,
  onClickPoint,
  onMarkerSelect,
  emphasizedMarkerId,
  onMarkerHover,
  focusRequest,
  hideLeftControls = false,
  overlayEnabled: overlayEnabledProp,
  onOverlayEnabledChange,
  selectedOverlayVersion: selectedOverlayVersionProp,
  onOverlayVersionChange,
  selectedPolygonIds: selectedPolygonIdsProp,
  onSelectedPolygonIdsChange,
}) => {
  const { t } = useTranslation("map");
  const provider = useMapProviderStore((s) => s.provider);

  // Each of these is controlled when the matching prop/handler is supplied,
  // otherwise it falls back to MapView's own internal state (unchanged for
  // existing callers like location/machine detail).
  const [overlayEnabledInternal, setOverlayEnabledInternal] = useState(true);
  const overlayEnabled = overlayEnabledProp ?? overlayEnabledInternal;
  const setOverlayEnabled = onOverlayEnabledChange ?? setOverlayEnabledInternal;

  const [selectedPolygonIdsInternal, setSelectedPolygonIdsInternal] = useState<number[]>([]);
  const selectedPolygonIds = selectedPolygonIdsProp ?? selectedPolygonIdsInternal;
  const setSelectedPolygonIds = onSelectedPolygonIdsChange ?? setSelectedPolygonIdsInternal;

  const { polygons: selectedPolygons } = useGetPolygonsByIdsForSelector(selectedPolygonIds);
  const [showLeftControls, setShowLeftControls] = useState<boolean>(true);

  const [selectedOverlayVersionInternal, setSelectedOverlayVersionInternal] = useState<
    string | undefined
  >(overlayVersion);
  const selectedOverlayVersion = selectedOverlayVersionProp ?? selectedOverlayVersionInternal;
  const setSelectedOverlayVersion = onOverlayVersionChange ?? setSelectedOverlayVersionInternal;
  const isVersionControlled = onOverlayVersionChange !== undefined;
  const [hiddenPolygonIds, setHiddenPolygonIds] = useState<Set<number>>(new Set());
  const [currentZoom, setCurrentZoom] = useState(0);
  const [isInBounds, setIsInBounds] = useState(true);
  const canvasRef = useRef<MapCanvasHandle | null>(null);
  // Last focus nonce we acted on — guards the effect so a re-render (e.g. a
  // zoom change) never re-fires the same request, only a genuinely new one.
  const lastFocusNonceRef = useRef<number | null>(null);

  // React to a camera-focus intent from the page. `hard` pans + zooms in;
  // `soft` always pans but only zooms when the alert is off-screen or the view
  // is too far out (so stepping through nearby pins doesn't re-zoom on every press).
  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.nonce === lastFocusNonceRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    lastFocusNonceRef.current = focusRequest.nonce;
    const { lat, lng, mode } = focusRequest;
    if (mode === "hard" || mode === "max") {
      canvas.centerOn(lat, lng, mode === "max" ? MAX_ALERT_ZOOM : MARKER_FOCUS_ZOOM);
      return;
    }
    canvas.panTo(lat, lng);
    if (!canvas.isInView(lat, lng) || currentZoom < SOFT_FOCUS_MIN_ZOOM) {
      canvas.centerOn(lat, lng, MARKER_FOCUS_ZOOM);
    }
  }, [focusRequest, currentZoom]);

  const togglePolygonVisibility = useCallback((id: number) => {
    setHiddenPolygonIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Fetch available overlay versions for this location
  const { data: versionsData, isLoading: isVersionsLoading } = useGetMapOverlayVersions({
    location_id: locationId!,
  });

  // Get versions list and find active version
  const overlayVersions = versionsData?.data?.versions || [];
  const activeVersion = overlayVersions.find((v) => v.status === "active");

  // Set default version to active when versions are loaded (uncontrolled only —
  // a controlling parent owns the default).
  useEffect(() => {
    if (isVersionControlled) return;
    if (!selectedOverlayVersionInternal && activeVersion) {
      setSelectedOverlayVersionInternal(activeVersion.version);
    }
  }, [activeVersion, selectedOverlayVersionInternal, isVersionControlled]);

  // Reset selected version when location changes (uncontrolled only).
  useEffect(() => {
    if (isVersionControlled) return;
    setSelectedOverlayVersionInternal(undefined);
  }, [locationId, isVersionControlled]);

  // Convert polygon coordinates to CoordinateSet format (for internal polygon controls)
  const internalPolygonCoordinates: CoordinateSet[] = useMemo(() => {
    if (selectedPolygons.length === 0) return [];
    return selectedPolygons
      .filter((p) => p.coordinates && p.coordinates.length > 0 && !hiddenPolygonIds.has(p.id))
      .map((polygon) => ({
        name: polygon.name,
        color: stringToColor(polygon.name),
        path: polygon.coordinates.map((coord) => ({
          lat: coord.latitude,
          lng: coord.longitude,
        })),
        isActive: true,
      }));
  }, [selectedPolygons, hiddenPolygonIds]);

  // Use external polygon coordinates if provided, otherwise use internal
  const polygonCoordinates =
    polygonCoordinatesProp.length > 0 ? polygonCoordinatesProp : internalPolygonCoordinates;

  // Fetch overlay data with selected version
  const { data: overlayData } = useGetMapOverlay({
    location_id: locationId!,
    version: selectedOverlayVersion,
  });

  // Combine all coordinates
  const allCoordinates = useMemo(
    () => [...basicCoordinates, ...polygonCoordinates],
    [basicCoordinates, polygonCoordinates],
  );

  // Initialize shell-level hooks
  const resize = useMapResize({ resizable, initialHeight, storageKey });
  const tooltip = useMapTooltip();

  // Calculate map center and check empty state
  const mapCenter = useMemo(() => calculateMapCenter(allCoordinates), [allCoordinates]);
  const showEmpty = shouldShowEmptyState(allCoordinates, isEmpty);

  const overlayConfig = overlayData?.data?.available ? overlayData.data.config ?? null : null;

  // Merge neutral map options. While an overlay is active, cap the map's max
  // zoom to the overlay's native `max_zoom`: the overlay's tile URL returns ''
  // above that level (blanking the overlay), and true tile overzoom isn't
  // possible through a URL template. Google clamps any setZoom() (e.g. an
  // alert's zoom-to-20) to the cap, so the alert max-zoom lands at the overlay's
  // deepest level with the overlay still visible. No overlay → full zoom.
  const mergedOptions = useMemo<MapViewOptions>(
    () => {
      const base = { ...DEFAULT_MAP_VIEW_OPTIONS, ...options };
      return overlayConfig && overlayEnabled
        ? { ...base, maxZoom: overlayConfig.max_zoom }
        : base;
    },
    [options, overlayConfig, overlayEnabled],
  );

  /**
   * Handle marker click - trigger timeline selection if a time is present,
   * route the selection, and zoom the map toward the clicked point.
   */
  const handleMarkerClick = ({
    markerId,
    lat,
    lng,
    time,
  }: {
    markerId: string;
    lat: number;
    lng: number;
    time?: Date;
  }) => {
    if (time && onClickPoint) {
      onClickPoint(time);
    }
    if (onMarkerSelect) {
      onMarkerSelect(markerId);
      // Focus the clicked point so selecting a pin zooms it into view. Alert
      // pins zoom all the way in ({@link MAX_ALERT_ZOOM}); location/machine pins
      // stop at the gentler {@link MARKER_FOCUS_ZOOM}.
      const isAlert = parseSelectionId(markerId)?.type === "alert";
      canvasRef.current?.centerOn(lat, lng, isAlert ? MAX_ALERT_ZOOM : MARKER_FOCUS_ZOOM);
    }
  };

  /**
   * Center map to coordinates
   */
  const handleCenterToCoordinates = () => {
    if (mapCenter) {
      canvasRef.current?.centerOn(mapCenter.lat, mapCenter.lng);
    }
  };

  // Reframe when the selected location changes (e.g. via the location switcher),
  // not on every coordinate churn (alerts polling) — guarded on locationId.
  // `fitTo` (not `centerOn`) because the new location is a different subject:
  // the camera has to be able to zoom back OUT of, say, an alert at zoom 20.
  //
  // The change is only marked handled once the new location's markers have
  // actually arrived: the machines list and its GPS land a tick after the id
  // flips, and `calculateMapCenter` answers {0,0} for an empty set — centering
  // on that would fling the map into the Atlantic and never recover.
  const prevLocationId = useRef(locationId);
  useEffect(() => {
    if (locationId === prevLocationId.current) return;
    const points = allCoordinates.flatMap((c) => c.path);
    if (points.length === 0) return;
    prevLocationId.current = locationId;
    canvasRef.current?.fitTo(points);
  }, [locationId, allCoordinates]);

  /**
   * Render resize handle
   */
  const renderResizeHandle = () => {
    if (!resizable) return null;

    return (
      <div
        className="absolute bottom-0 left-0 right-0 h-3 cursor-row-resize bg-transparent"
        onMouseDown={resize.beginDrag}
        title={t("view.resizeHandle")}
      >
        <div className="mx-auto h-1 w-24 rounded bg-muted" />
      </div>
    );
  };

  /**
   * Render loading state
   */
  if (isLoading) {
    return (
      <div className="relative w-full" style={{ height: resize.containerHeight }}>
        <div className="h-full bg-muted/50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            <span className="text-sm">{loadingText ?? t("view.loading")}</span>
          </div>
        </div>
        {renderResizeHandle()}
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (showEmpty) {
    return (
      <div className="relative w-full" style={{ height: resize.containerHeight }}>
        <div className="h-full flex items-center justify-center p-4">
          <EmptyState
            title={emptyStateTitle ?? t("view.emptyTitle")}
            description={emptyStateDescription ?? t("view.emptyDescription")}
            compact={initialHeight <= 300}
            className="w-full h-full"
          />
        </div>
        {renderResizeHandle()}
      </div>
    );
  }

  const Canvas = provider === "libre" ? LibreMapCanvas : GoogleMapCanvas;

  return (
    <div className="relative w-full" style={{ height: resize.containerHeight }}>
      <Canvas
        ref={canvasRef}
        basicCoordinates={basicCoordinates}
        polygonCoordinates={polygonCoordinates}
        options={mergedOptions}
        center={mapCenter}
        height={initialHeight}
        overlay={overlayConfig}
        overlayEnabled={overlayEnabled}
        onMarkerClick={handleMarkerClick}
        onClickPoint={onClickPoint}
        emphasizedMarkerId={emphasizedMarkerId}
        onMarkerHover={onMarkerHover}
        onTooltipShow={tooltip.showTooltip}
        onTooltipHide={tooltip.hideTooltip}
        onZoomChange={setCurrentZoom}
        onInBoundsChange={setIsInBounds}
      />

      {/* Top Left Controls */}
      {!hideLeftControls && showLeftControls && (
        <div className={"flex flex-col absolute left-4 top-4 z-10 gap-2"}>
          {/* Custom Left Content */}
          {renderLeftContent}

          {/* Version Selector */}
          {overlayVersions.length > 1 && (
            <MapOverlayVersionSelector
              versions={overlayVersions}
              selectedVersion={selectedOverlayVersion}
              onVersionChange={setSelectedOverlayVersion}
              isLoading={isVersionsLoading}
            />
          )}

          {/* Overlay Controls */}
          {overlayConfig && (
            <MapOverlayControls
              config={overlayConfig}
              currentZoom={currentZoom}
              isInBounds={isInBounds}
              isEnabled={overlayEnabled}
              onToggle={setOverlayEnabled}
            />
          )}

          {/* Polygon Controls */}
          {showPolygonControls && (
            <div className="">
              <PolygonControls
                selectedPolygonIds={selectedPolygonIds}
                onSelectionChange={setSelectedPolygonIds}
                locationId={locationId}
              />
            </div>
          )}

          {/* Polygon Legend */}
          {showPolygonControls && selectedPolygons.length > 0 && (
            <PolygonLegend
              selectedPolygons={selectedPolygons}
              hiddenPolygonIds={hiddenPolygonIds}
              onToggleVisibility={togglePolygonVisibility}
            />
          )}
        </div>
      )}

      {/* Bottom Left Controls */}
      {controlsUIEnable && (
        <div className="flex flex-col absolute left-4 bottom-4 z-10 gap-2">
          <div className="flex flex-col gap-1 bg-card/95 backdrop-blur-sm rounded-lg border shadow-sm">
            {/* Toggle Left Controls */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowLeftControls(!showLeftControls)}
              className="h-10 w-10"
              title={showLeftControls ? t("view.hideControls") : t("view.showControls")}
            >
              {showLeftControls ? (
                <PanelLeftClose className="h-5 w-5" />
              ) : (
                <PanelLeftOpen className="h-5 w-5" />
              )}
            </Button>

            {/* Center to Coordinates */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCenterToCoordinates}
              className="h-10 w-10"
              title={t("view.centerToCoordinates")}
            >
              <Crosshair className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Resize Handle */}
      {renderResizeHandle()}

      {/* Polygon Tooltip */}
      {tooltip.tooltip && (
        <div
          className="pointer-events-none fixed z-50 rounded-md bg-popover/95 backdrop-blur-sm border shadow-md px-3 py-1.5 text-sm"
          style={{
            left: tooltip.tooltip.x + 12,
            top: tooltip.tooltip.y - 12,
          }}
        >
          <div className="flex items-center gap-2">
            {tooltip.tooltip.color && (
              <div
                className="h-3 w-3 rounded-full border border-white/50"
                style={{ backgroundColor: tooltip.tooltip.color }}
              />
            )}
            <span className="font-medium">{tooltip.tooltip.name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;
